import React, { useCallback, useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE } from '../utils/api';
import AdminAppReleaseManager from './AdminAppReleaseManager';
import AdminNetworkAccessManager from './AdminNetworkAccessManager';
import AdminMalwareScanManager from './AdminMalwareScanManager';
import AdminPaymentSettingsManager from './AdminPaymentSettingsManager';
import AdminWorkflowScriptAccessManager from './AdminWorkflowScriptAccessManager';
import AdminPlanRequestManager from './AdminPlanRequestManager';

function AdminDashboard() {
const [activeTab, setActiveTab] = useState('pending');
const [plugins, setPlugins] = useState([]);
const [pendingCount, setPendingCount] = useState(0);
const [pendingWorkflowCount, setPendingWorkflowCount] = useState(0);
const [selectedPlugin, setSelectedPlugin] = useState(null);
const [selectedWorkflow, setSelectedWorkflow] = useState(null);
const [loading, setLoading] = useState(false);
const [token, setToken] = useState(localStorage.getItem('token'));
const isFullAdmin = localStorage.getItem('userRole') === 'Admin';

const loadPlugins = useCallback(async () => {
setLoading(true);
try {
const endpoint = activeTab === 'pending'
  ? '/plugin/pending'
  : activeTab === 'approved'
    ? '/plugin/approved'
    : activeTab === 'pendingWorkflows'
      ? '/workflow/pending'
      : '/workflow/approved';
const response = await axios.get(`${API_BASE}${endpoint}`, {
headers: { Authorization: `Bearer ${token}` }
});
setPlugins(response.data);
if (activeTab === 'pending') {
setPendingCount(response.data.length);
}
if (activeTab === 'pendingWorkflows') {
setPendingWorkflowCount(response.data.length);
}
} catch (error) {
console.error('Error loading plugins:', error);
alert('Failed to load plugins');
}
setLoading(false);
}, [activeTab, token]);

const loadPendingCount = useCallback(async () => {
try {
const [pluginResponse, workflowResponse] = await Promise.all([
axios.get(`${API_BASE}/plugin/pending`, { headers: { Authorization: `Bearer ${token}` } }),
axios.get(`${API_BASE}/workflow/pending`, { headers: { Authorization: `Bearer ${token}` } })
]);
setPendingCount(pluginResponse.data.length);
setPendingWorkflowCount(workflowResponse.data.length);
} catch (error) {
console.error('Error loading pending plugin count:', error);
}
}, [token]);

useEffect(() => {
if (token && ['pending', 'approved', 'pendingWorkflows', 'approvedWorkflows'].includes(activeTab)) {
loadPlugins();
}
}, [activeTab, token, loadPlugins]);

useEffect(() => {
if (token) {
loadPendingCount();
}
}, [token, loadPendingCount]);


const approvePlugin = async (pluginId) => {
if (!window.confirm('Are you sure you want to approve this plugin?')) return;

try {
  await axios.post(`${API_BASE}/plugin/${pluginId}/approve`, {}, {
    headers: { Authorization: `Bearer ${token}` }
  });
  alert('Plugin approved successfully');
  loadPlugins();
} catch (error) {
  console.error('Error approving plugin:', error);
  alert('Failed to approve plugin');
}

};

const rejectPlugin = async (pluginId) => {
const reason = window.prompt('Enter rejection reason:');
if (!reason) return;

try {
  await axios.post(`${API_BASE}/plugin/${pluginId}/reject`, 
    { reason },
    { headers: { Authorization: `Bearer ${token}` }}
  );
  alert('Plugin rejected');
  loadPlugins();
} catch (error) {
  console.error('Error rejecting plugin:', error);
  alert('Failed to reject plugin');
}

};

const approveWorkflow = async (workflowId) => {
if (!window.confirm('Are you sure you want to approve this workflow?')) return;
try {
  await axios.post(`${API_BASE}/workflow/${workflowId}/approve`, {}, {
    headers: { Authorization: `Bearer ${token}` }
  });
  alert('Workflow approved successfully');
  loadPlugins();
  loadPendingCount();
} catch (error) {
  alert(error.response?.data?.message || 'Failed to approve workflow');
}
};

const rejectWorkflow = async (workflowId) => {
const reason = window.prompt('Enter rejection reason:');
if (!reason) return;
try {
  await axios.post(`${API_BASE}/workflow/${workflowId}/reject`, { reason }, {
    headers: { Authorization: `Bearer ${token}` }
  });
  alert('Workflow rejected');
  loadPlugins();
  loadPendingCount();
} catch (error) {
  alert(error.response?.data?.message || 'Failed to reject workflow');
}
};

const login = async (email, password) => {
try {
const response = await axios.post(`${API_BASE}/account/login`, {
email,
password
});
setToken(response.data.token);
localStorage.setItem('token', response.data.token);
localStorage.setItem('userId', response.data.userId);
localStorage.setItem('userEmail', response.data.email);
localStorage.setItem('userName', response.data.fullName);
localStorage.setItem('userRole', response.data.role || 'User');
} catch (error) {
alert(error.response?.data?.message || 'Login failed');
}
};

if (!token) {
return <LoginForm onLogin={login} />;
}

return (
<div style={styles.container}>
<nav style={styles.navbar}>
  <h1 style={styles.logo}>Plugin Repository</h1>
  <div style={styles.navRight}>
    <span style={styles.userName}>Admin</span>
    <button
      onClick={() => {
        setToken(null);
        localStorage.removeItem('token');
      }}
      style={styles.logoutBtn}
    >
      Logout
    </button>
  </div>
</nav>

  <div style={styles.layout}>
    <aside style={styles.sidebar}>
      <button
        style={{...styles.sidebarBtn, ...(activeTab === 'pending' ? styles.activeSidebarBtn : {})}}
        onClick={() => setActiveTab('pending')}
      >
        🕒 Pending Approval ({pendingCount})
      </button>
      <button
        style={{...styles.sidebarBtn, ...(activeTab === 'approved' ? styles.activeSidebarBtn : {})}}
        onClick={() => setActiveTab('approved')}
      >
        ✅ Approved Plugins
      </button>
      <button
        style={{...styles.sidebarBtn, ...(activeTab === 'pendingWorkflows' ? styles.activeSidebarBtn : {})}}
        onClick={() => setActiveTab('pendingWorkflows')}
      >
        🕒 Pending Workflows ({pendingWorkflowCount})
      </button>
      <button
        style={{...styles.sidebarBtn, ...(activeTab === 'approvedWorkflows' ? styles.activeSidebarBtn : {})}}
        onClick={() => setActiveTab('approvedWorkflows')}
      >
        ✅ Approved Workflows
      </button>
      <button
        style={{...styles.sidebarBtn, ...(activeTab === 'appReleases' ? styles.activeSidebarBtn : {})}}
        onClick={() => setActiveTab('appReleases')}
      >
        App Releases
      </button>
      {isFullAdmin && (
        <button
          style={{...styles.sidebarBtn, ...(activeTab === 'planRequests' ? styles.activeSidebarBtn : {})}}
          onClick={() => setActiveTab('planRequests')}
        >
          Plan Access Requests
        </button>
      )}
      {isFullAdmin && (
        <button
          style={{...styles.sidebarBtn, ...(activeTab === 'workflowScriptAccess' ? styles.activeSidebarBtn : {})}}
          onClick={() => setActiveTab('workflowScriptAccess')}
        >
          Workflow Script Access
        </button>
      )}
      {isFullAdmin && (
        <button
          style={{...styles.sidebarBtn, ...(activeTab === 'paymentSettings' ? styles.activeSidebarBtn : {})}}
          onClick={() => setActiveTab('paymentSettings')}
        >
          Payment Settings
        </button>
      )}
      {isFullAdmin && (
        <button
          style={{...styles.sidebarBtn, ...(activeTab === 'malwareScanning' ? styles.activeSidebarBtn : {})}}
          onClick={() => setActiveTab('malwareScanning')}
        >
          🛡️ Malware Scanning
        </button>
      )}
      {isFullAdmin && (
        <button
          style={{...styles.sidebarBtn, ...(activeTab === 'networkAccess' ? styles.activeSidebarBtn : {})}}
          onClick={() => setActiveTab('networkAccess')}
        >
          🔒 Admin IP Access
        </button>
      )}
    </aside>

    <main style={styles.main}>
      <div style={styles.tabHeader}>
        <h2>{activeTab === 'pending'
          ? 'Pending Approval'
          : activeTab === 'approved'
            ? 'Approved Plugins'
            : activeTab === 'pendingWorkflows'
              ? 'Pending Workflows'
              : activeTab === 'approvedWorkflows'
                ? 'Approved Workflows'
            : activeTab === 'appReleases'
              ? 'App Releases'
              : activeTab === 'malwareScanning'
                ? 'Malware Scanning'
                : activeTab === 'workflowScriptAccess'
                  ? 'Workflow Script Access'
                : activeTab === 'planRequests'
                  ? 'Plan Access Requests'
                : activeTab === 'paymentSettings'
                  ? 'Payment Settings'
                : 'Admin IP Access'}</h2>
      </div>

      {activeTab === 'appReleases' ? (
        <AdminAppReleaseManager />
      ) : activeTab === 'malwareScanning' ? (
        <AdminMalwareScanManager />
      ) : activeTab === 'paymentSettings' ? (
        <AdminPaymentSettingsManager />
      ) : activeTab === 'workflowScriptAccess' ? (
        <AdminWorkflowScriptAccessManager />
      ) : activeTab === 'planRequests' ? (
        <AdminPlanRequestManager />
      ) : activeTab === 'networkAccess' ? (
        <AdminNetworkAccessManager />
      ) : loading ? (
        <div style={styles.loading}>Loading...</div>
      ) : (
        <div>
          {plugins.length === 0 ? (
            <div style={styles.emptyState}>{activeTab.includes('Workflows') ? 'No workflows found' : 'No plugins found'}</div>
          ) : (
            <div style={styles.pluginGrid}>
              {activeTab.includes('Workflows')
                ? plugins.map(workflow => (
                  <WorkflowReviewCard
                    key={workflow.id}
                    workflow={workflow}
                    onApprove={approveWorkflow}
                    onReject={rejectWorkflow}
                    onViewDetails={setSelectedWorkflow}
                    showActions={activeTab === 'pendingWorkflows'}
                  />
                ))
                : plugins.map(plugin => (
                  <PluginCard
                    key={plugin.id}
                    plugin={plugin}
                    onApprove={approvePlugin}
                    onReject={rejectPlugin}
                    onViewDetails={setSelectedPlugin}
                    showActions={activeTab === 'pending'}
                  />
                ))}
            </div>
          )}
        </div>
      )}
    </main>
  </div>

  {selectedPlugin && (
    <PluginDetailsModal
      plugin={selectedPlugin}
      token={token}
      onClose={() => setSelectedPlugin(null)}
    />
  )}
  {selectedWorkflow && (
    <WorkflowReviewModal workflow={selectedWorkflow} token={token} onClose={() => setSelectedWorkflow(null)} />
  )}
</div>

);
}

function LoginForm({ onLogin }) {
const [email, setEmail] = useState('');
const [password, setPassword] = useState('');

const handleSubmit = (e) => {
e.preventDefault();
onLogin(email, password);
};

return (
<div style={styles.loginContainer}>
<form onSubmit={handleSubmit} style={styles.loginForm}>
<h2>Admin Login</h2>
<input
type="email"
placeholder="Email"
value={email}
onChange={(e) => setEmail(e.target.value)}
style={styles.input}
required
/>
<input
type="password"
placeholder="Password"
value={password}
onChange={(e) => setPassword(e.target.value)}
style={styles.input}
required
/>
<button type="submit" style={styles.submitButton}>Login</button>
<p style={styles.loginSecurityNote}>
10 failed login attempts within 15 minutes trigger a 15-minute IP lockout.
</p>
</form>
</div>
);
}

function PluginCard({ plugin, onApprove, onReject, onViewDetails, showActions }) {
return (
<div style={styles.pluginCard}>
<h3>{plugin.name}</h3>
<p><strong>Author:</strong> {plugin.authorName}</p>
<p><strong>Version:</strong> {plugin.version}</p>
<p style={styles.description}>{plugin.description}</p>
<div style={styles.tags}>
{plugin.tags?.split(',').map((tag, i) => (
<span key={i} style={styles.tag}>{tag.trim()}</span>
))}
</div>
<div style={styles.stats}>
<span>⭐ {plugin.averageRating.toFixed(1)}</span>
<span>📥 {plugin.downloads}</span>
<span>📅 {new Date(plugin.createdAt).toLocaleDateString()}</span>
</div>
<div style={styles.cardActions}>
<button onClick={() => onViewDetails(plugin)} style={styles.viewButton}>
View Details
</button>
{showActions && (
<>
<button
onClick={() => onApprove(plugin.id)}
style={{...styles.actionButton, ...styles.approveButton}}
>
✓ Approve
</button>
<button
onClick={() => onReject(plugin.id)}
style={{...styles.actionButton, ...styles.rejectButton}}
>
✗ Reject
</button>
</>
)}
</div>
</div>
);
}

async function openAdminZipDownload(kind, id, token) {
try {
const response = await axios.get(`${API_BASE}/${kind}/${id}/admin-download`, {
headers: { Authorization: `Bearer ${token}` }
});
const downloadUrl = response.data?.downloadUrl;
if (!downloadUrl) throw new Error('The server did not return a download link.');
const secureDownloadUrl = window.location.protocol === 'https:' && downloadUrl.startsWith('http://')
  ? downloadUrl.replace(/^http:/, 'https:')
  : downloadUrl;
window.open(secureDownloadUrl, '_blank', 'noopener,noreferrer');
} catch (error) {
alert(error.response?.data?.message || error.message || 'Unable to get the admin download link.');
}
}

function PluginDetailsModal({ plugin, token, onClose }) {
return (
<div style={styles.modalOverlay} onClick={onClose}>
<div style={styles.modal} onClick={(e) => e.stopPropagation()}>
<button style={styles.closeButton} onClick={onClose}>×</button>
<h2>{plugin.name}</h2>
<div style={styles.modalActions}>
<button type="button" onClick={() => openAdminZipDownload('plugin', plugin.id, token)} style={styles.downloadButton}>Download ZIP for testing</button>
</div>
<div style={styles.modalContent}>
<div style={styles.detailRow}>
<strong>Author:</strong> {plugin.authorName}
</div>
<div style={styles.detailRow}>
<strong>Version:</strong> {plugin.version}
</div>
<div style={styles.detailRow}>
<strong>Status:</strong> {plugin.status}
</div>
<div style={styles.detailRow}>
<strong>Downloads:</strong> {plugin.downloads}
</div>
<div style={styles.detailRow}>
<strong>Rating:</strong> {plugin.averageRating.toFixed(1)} ⭐
</div>
<div style={styles.detailRow}>
<strong>Created:</strong> {new Date(plugin.createdAt).toLocaleString()}
</div>
<div style={styles.detailRow}>
<strong>Description:</strong>
<p>{plugin.description}</p>
</div>
<ParsedReadmeReview parsed={plugin.readmeParsed} readmeContent={plugin.readmeContent} assetBase={`${API_BASE}/plugin/${plugin.id}/assets?path=resources/`} />
<AdminFilePreview kind="plugin" id={plugin.id} token={token} />
{plugin.media && plugin.media.length > 0 && (
<div style={styles.detailRow}>
<strong>Media:</strong>
<div style={styles.mediaGrid}>
{plugin.media.map((media, i) => (
<div key={i} style={styles.mediaThumbnail}>
{media.type === 0 ? '🖼️ Image' : '🎥 Video'}
</div>
))}
</div>
</div>
)}
</div>
</div>
</div>
);
}

function ParsedReadmeReview({ parsed, readmeContent, assetBase }) {
const additionalSections = (parsed?.additionalSections || [])
  .filter(section => !/^seo\s+keywords?$/i.test(String(section.title || '').trim()));
const hasStructuredContent = Boolean(
  parsed && (
    parsed.summary ||
    parsed.description ||
    parsed.installation ||
    parsed.requirements ||
    parsed.serverRequirements ||
    parsed.clientRequirements ||
    parsed.specialNoteOnRequirements ||
    parsed.faq?.length ||
    parsed.changelog?.length ||
    parsed.screenshots?.length ||
    additionalSections.length
  )
);

if (!hasStructuredContent) {
return (
<div style={styles.detailRow}>
<strong>README:</strong>
<pre style={styles.readme}>{readmeContent || 'No README content was parsed.'}</pre>
</div>
);
}

return (
<div style={styles.parsedReview}>
<div style={styles.detailRow}>
<strong>Parsed README content</strong>
{(parsed.contributors || parsed.types?.length || parsed.tags?.length || parsed.requiresAtLeast || parsed.testedUpTo || parsed.stableTag || parsed.license) && (
<div style={styles.parsedMeta}>
{parsed.contributors && <div><strong>Contributors:</strong> {parsed.contributors}</div>}
{parsed.types?.length > 0 && <div><strong>Type:</strong> {parsed.types.join(', ')}</div>}
{parsed.tags?.length > 0 && <div><strong>Tags:</strong> {parsed.tags.join(', ')}</div>}
{parsed.requiresAtLeast && <div><strong>Requires at least:</strong> {parsed.requiresAtLeast}</div>}
{parsed.testedUpTo && <div><strong>Tested up to:</strong> {parsed.testedUpTo}</div>}
{parsed.stableTag && <div><strong>Stable tag:</strong> {parsed.stableTag}</div>}
{parsed.license && <div><strong>License:</strong> {parsed.license}</div>}
</div>
)}
</div>
{parsed.summary && <ReviewSection title="Summary"><p>{parsed.summary}</p></ReviewSection>}
{parsed.description && <ReviewSection title="Description"><p style={styles.parsedParagraph}>{parsed.description}</p></ReviewSection>}
{parsed.installation && <ReviewSection title="Installation"><pre style={styles.readme}>{parsed.installation}</pre></ReviewSection>}
{additionalSections.map((section, index) => (
<ReviewSection key={`${section.title}-${index}`} title={section.title || `Section ${index + 1}`}>
<pre style={styles.readme}>{section.content}</pre>
</ReviewSection>
))}
{parsed.screenshots?.length > 0 && (
<ReviewSection title="Screenshots / media parsed from README">
<div style={styles.reviewMediaList}>
{parsed.screenshots.map((shot, index) => {
const file = String(shot.file || '').replace(/^resources[\\/]/i, '');
const src = assetBase && file ? `${assetBase}${encodeURIComponent(file)}` : '';
return (
<div key={`${shot.file}-${index}`} style={styles.reviewMediaItem}>
{src && <img src={src} alt={shot.caption || shot.file} style={styles.reviewMediaImage} onError={event => { event.currentTarget.style.display = 'none'; }} />}
<div><strong>{shot.file}</strong>{shot.caption && <p>{shot.caption}</p>}{shot.mediaType && <small>{shot.mediaType}</small>}</div>
</div>
);
})}
</div>
</ReviewSection>
)}
{parsed.serverRequirements && <ReviewSection title="Server Requirements"><pre style={styles.readme}>{parsed.serverRequirements}</pre></ReviewSection>}
{parsed.clientRequirements && <ReviewSection title="Client Requirements"><pre style={styles.readme}>{parsed.clientRequirements}</pre></ReviewSection>}
{parsed.requirements && <ReviewSection title="Requirements"><pre style={styles.readme}>{parsed.requirements}</pre></ReviewSection>}
{parsed.specialNoteOnRequirements && <ReviewSection title="Special Note on Requirements"><pre style={styles.readme}>{parsed.specialNoteOnRequirements}</pre></ReviewSection>}
{parsed.faq?.length > 0 && <ReviewSection title="FAQ">{parsed.faq.map((item, index) => <div key={`${item.question}-${index}`} style={styles.faqItem}><strong>{item.question}</strong><p>{item.answer}</p></div>)}</ReviewSection>}
{parsed.changelog?.length > 0 && <ReviewSection title="Changelog">{parsed.changelog.map((item, index) => <div key={`${item.version}-${index}`} style={styles.faqItem}><strong>{item.version}</strong><pre style={styles.readme}>{item.notes}</pre></div>)}</ReviewSection>}
<ReviewSection title="Raw README"><pre style={styles.readme}>{readmeContent}</pre></ReviewSection>
</div>
);
}

function ReviewSection({ title, children }) {
return <section style={styles.reviewSection}><h3 style={styles.reviewSectionTitle}>{title}</h3>{children}</section>;
}

function WorkflowReviewCard({ workflow, onApprove, onReject, onViewDetails, showActions }) {
const metadata = workflow.workflowMetadata || {};
return (
<div style={styles.pluginCard}>
<h3>{workflow.name}</h3>
<p><strong>Author:</strong> {workflow.authorName}</p>
<p><strong>Type:</strong> {metadata.packageType === 'bundle' ? 'Workflow Bundle' : 'Plain Workflow'}</p>
<p style={styles.description}>{workflow.description}</p>
<div style={styles.stats}>
<span>{Number(metadata.nodeCount || 0)} nodes</span>
<span>{Number(metadata.subflowCount || 0)} subflows</span>
<span>{(metadata.skills || []).length} skills</span>
</div>
<div style={styles.cardActions}>
<button onClick={() => onViewDetails(workflow)} style={styles.viewButton}>View Details</button>
{showActions && <>
<button onClick={() => onApprove(workflow.id)} style={{...styles.actionButton, ...styles.approveButton}}>✓ Approve</button>
<button onClick={() => onReject(workflow.id)} style={{...styles.actionButton, ...styles.rejectButton}}>✗ Reject</button>
</>}
</div>
</div>
);
}

function WorkflowReviewModal({ workflow, token, onClose }) {
const metadata = workflow.workflowMetadata || {};
return (
<div style={styles.modalOverlay} onClick={onClose}>
<div style={styles.modal} onClick={(event) => event.stopPropagation()}>
<button style={styles.closeButton} onClick={onClose}>×</button>
<h2>{workflow.name}</h2>
<div style={styles.modalActions}>
<button type="button" onClick={() => openAdminZipDownload('workflow', workflow.id, token)} style={styles.downloadButton}>Download ZIP for testing</button>
</div>
<div style={styles.modalContent}>
<div style={styles.detailRow}><strong>Author:</strong> {workflow.authorName}</div>
<div style={styles.detailRow}><strong>Status:</strong> {workflow.status}</div>
<div style={styles.detailRow}><strong>Package:</strong> {metadata.packageType === 'bundle' ? 'Bundle' : 'Plain export'}</div>
<div style={styles.detailRow}><strong>Workflow JSON:</strong> {metadata.workflowFile || 'Not available'}</div>
<div style={styles.detailRow}><strong>Nodes:</strong> {Number(metadata.nodeCount || 0)}</div>
<div style={styles.detailRow}><strong>Subflows:</strong> {Number(metadata.subflowCount || 0)}</div>
<div style={styles.detailRow}><strong>Description:</strong><p>{workflow.description || 'No description supplied.'}</p></div>
{(metadata.skills || []).length > 0 && <div style={styles.detailRow}>
<strong>Skills:</strong>
<div style={styles.tags}>{metadata.skills.map(skill => <span key={skill} style={styles.tag}>{skill}</span>)}</div>
</div>}
{(workflow.tags || metadata.tags?.length > 0) && <div style={styles.detailRow}>
<strong>Tags:</strong>
<div style={styles.tags}>{String(workflow.tags || '').split(',').filter(Boolean).map(tag => <span key={tag.trim()} style={styles.tag}>{tag.trim()}</span>)}{(metadata.tags || []).map(tag => <span key={tag} style={styles.tag}>{tag}</span>)}</div>
</div>}
{(metadata.images || []).length > 0 && <div style={styles.detailRow}>
<strong>Images parsed from package:</strong>
<div style={styles.reviewMediaList}>{metadata.images.map((image, index) => <div key={`${image}-${index}`} style={styles.reviewMediaItem}><strong>{image}</strong></div>)}</div>
</div>}
{(metadata.examples || []).length > 0 && <div style={styles.detailRow}>
<strong>Examples:</strong>
{metadata.examples.map((example, index) => <ReviewSection key={`${example.path}-${index}`} title={example.title || example.path || `Example ${index + 1}`}><pre style={styles.readme}>{example.content}</pre></ReviewSection>)}
</div>}
<ParsedReadmeReview parsed={workflow.readmeParsed} readmeContent={workflow.readmeContent} />
<AdminFilePreview kind="workflow" id={workflow.id} token={token} />
</div>
</div>
</div>
);
}

function flattenFileTree(nodes, depth = 0) {
return (nodes || []).flatMap(node => {
const current = [{ ...node, depth }];
return node.children?.length ? current.concat(flattenFileTree(node.children, depth + 1)) : current;
});
}

function AdminFilePreview({ kind, id, token }) {
const [files, setFiles] = useState([]);
const [selected, setSelected] = useState(null);
const [loadingFiles, setLoadingFiles] = useState(false);
const [message, setMessage] = useState('');
const flatFiles = flattenFileTree(files);

useEffect(() => {
let active = true;
const loadFiles = async () => {
setLoadingFiles(true);
setMessage('');
try {
const response = await axios.get(`${API_BASE}/${kind}/${id}/files`, {
headers: token ? { Authorization: `Bearer ${token}` } : {}
});
if (active) setFiles(response.data || []);
} catch (error) {
if (active) setMessage(error.response?.data?.message || 'Unable to load package files.');
} finally {
if (active) setLoadingFiles(false);
}
};
loadFiles();
return () => { active = false; };
}, [kind, id, token]);

const openFile = async (file) => {
if (file.type !== 'file' || !file.path) return;
setSelected({ path: file.path, loading: true, binary: false, content: '' });
try {
const response = await axios.get(`${API_BASE}/${kind}/${id}/file?path=${encodeURIComponent(file.path)}`, {
headers: token ? { Authorization: `Bearer ${token}` } : {}
});
setSelected({
path: response.data?.path || file.path,
loading: false,
binary: Boolean(response.data?.binary),
content: response.data?.content || ''
});
} catch (error) {
setSelected({
path: file.path,
loading: false,
binary: false,
content: error.response?.data?.message || 'Unable to load file content.'
});
}
};

return (
<ReviewSection title="Uploaded package files">
{loadingFiles && <p>Loading files...</p>}
{message && <div style={styles.notice}>{message}</div>}
{!loadingFiles && !message && flatFiles.length === 0 && <p>No repository files were found for this upload.</p>}
{flatFiles.length > 0 && (
<div style={styles.filePreviewLayout}>
<div style={styles.fileList}>
{flatFiles.map(file => (
<button
key={`${file.path || file.name}-${file.depth}`}
type="button"
disabled={file.type !== 'file'}
onClick={() => openFile(file)}
style={{
...styles.fileButton,
paddingLeft: 10 + Number(file.depth || 0) * 16,
...(selected?.path === file.path ? styles.activeFileButton : {}),
...(file.type !== 'file' ? styles.folderButton : {})
}}
>
{file.type === 'file' ? '📄' : '📁'} {file.name}
</button>
))}
</div>
<div style={styles.fileContent}>
{selected ? (
<>
<strong>{selected.path}</strong>
{selected.loading ? <p>Loading file...</p> : selected.binary ? <p>This file is binary or not previewable. Use the ZIP download to test it.</p> : <pre style={styles.readme}>{selected.content}</pre>}
</>
) : (
<p>Select a text file to preview its content.</p>
)}
</div>
</div>
)}
</ReviewSection>
);
}

const styles = {
container: {
minHeight: '100vh',
background: '#f5f6fa',
fontFamily: 'Arial, sans-serif'
},
navbar: {
background: 'white',
padding: '20px 40px',
boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
display: 'flex',
justifyContent: 'space-between',
alignItems: 'center'
},
logo: {
fontSize: '24px',
fontWeight: 'bold',
color: '#667eea',
margin: 0
},
navRight: {
display: 'flex',
alignItems: 'center',
gap: '20px'
},
userName: {
color: '#666'
},
logoutBtn: {
padding: '8px 16px',
background: '#e74c3c',
color: 'white',
border: 'none',
borderRadius: '6px',
cursor: 'pointer'
},
layout: {
display: 'flex',
maxWidth: '1400px',
margin: '0 auto',
padding: '20px'
},
sidebar: {
width: '250px',
background: 'white',
padding: '20px',
borderRadius: '8px',
height: 'fit-content',
marginRight: '20px',
boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
},
sidebarBtn: {
width: '100%',
padding: '12px 16px',
marginBottom: '10px',
background: 'transparent',
border: 'none',
borderRadius: '6px',
textAlign: 'left',
cursor: 'pointer',
fontSize: '15px',
transition: 'background 0.2s'
},
activeSidebarBtn: {
background: '#667eea',
color: 'white',
fontWeight: '600'
},
main: {
flex: 1,
background: 'white',
padding: '30px',
borderRadius: '8px',
boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
},
tabHeader: {
display: 'flex',
justifyContent: 'space-between',
alignItems: 'center',
marginBottom: '20px'
},
pluginGrid: {
display: 'grid',
gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
gap: '20px'
},
pluginCard: {
border: '1px solid #ddd',
borderRadius: '8px',
padding: '20px',
background: 'white',
boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
transition: 'transform 0.2s',
':hover': {
transform: 'translateY(-5px)'
}
},
description: {
color: '#666',
fontSize: '14px',
lineHeight: '1.5',
marginTop: '10px'
},
tags: {
display: 'flex',
flexWrap: 'wrap',
gap: '5px',
marginTop: '10px'
},
tag: {
background: '#ecf0f1',
padding: '4px 8px',
borderRadius: '4px',
fontSize: '12px',
color: '#555'
},
stats: {
display: 'flex',
justifyContent: 'space-between',
marginTop: '15px',
paddingTop: '15px',
borderTop: '1px solid #eee',
fontSize: '14px',
color: '#666'
},
cardActions: {
display: 'flex',
gap: '10px',
marginTop: '15px'
},
viewButton: {
flex: 1,
padding: '10px',
background: '#95a5a6',
color: 'white',
border: 'none',
borderRadius: '4px',
cursor: 'pointer'
},
actionButton: {
flex: 1,
padding: '10px',
border: 'none',
borderRadius: '4px',
cursor: 'pointer',
fontWeight: 'bold',
color: 'white'
},
approveButton: {
background: '#27ae60'
},
rejectButton: {
background: '#e74c3c'
},
loading: {
textAlign: 'center',
padding: '40px',
color: '#999'
},
loginContainer: {
display: 'flex',
justifyContent: 'center',
alignItems: 'center',
minHeight: '100vh',
background: '#f5f6fa'
},
loginForm: {
background: 'white',
padding: '40px',
borderRadius: '8px',
boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
width: '100%',
maxWidth: '400px'
},
input: {
width: '100%',
padding: '12px',
marginBottom: '15px',
border: '1px solid #ddd',
borderRadius: '4px',
fontSize: '16px'
},
submitButton: {
width: '100%',
padding: '12px',
background: '#3498db',
color: 'white',
border: 'none',
borderRadius: '4px',
fontSize: '16px',
cursor: 'pointer'
},
loginSecurityNote: {
fontSize: '12px',
lineHeight: '1.45',
color: '#64748b',
background: '#f8fafc',
border: '1px solid #e2e8f0',
borderRadius: '6px',
padding: '9px 10px',
marginTop: '12px'
},
modalOverlay: {
position: 'fixed',
top: 0,
left: 0,
right: 0,
bottom: 0,
background: 'rgba(0,0,0,0.7)',
display: 'flex',
justifyContent: 'center',
alignItems: 'center',
zIndex: 1000
},
modal: {
background: 'white',
padding: '30px',
borderRadius: '8px',
maxWidth: '800px',
maxHeight: '90vh',
overflow: 'auto',
position: 'relative',
width: '90%'
},
closeButton: {
position: 'absolute',
top: '10px',
right: '10px',
background: 'none',
border: 'none',
fontSize: '30px',
cursor: 'pointer',
color: '#999'
},
modalContent: {
marginTop: '20px'
},
modalActions: {
display: 'flex',
gap: '10px',
flexWrap: 'wrap',
marginTop: '12px',
paddingRight: '34px'
},
downloadButton: {
padding: '10px 14px',
background: '#2563eb',
color: 'white',
border: 'none',
borderRadius: '6px',
cursor: 'pointer',
fontWeight: '700'
},
detailRow: {
marginBottom: '15px',
paddingBottom: '15px',
borderBottom: '1px solid #eee'
},
parsedReview: {
display: 'grid',
gap: '12px'
},
parsedMeta: {
display: 'grid',
gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
gap: '8px',
marginTop: '10px',
padding: '12px',
background: '#eef2ff',
borderRadius: '6px',
fontSize: '13px'
},
parsedParagraph: {
whiteSpace: 'pre-wrap',
lineHeight: 1.6,
color: '#334155'
},
reviewSection: {
marginBottom: '15px',
padding: '15px',
border: '1px solid #e2e8f0',
borderRadius: '8px',
background: '#fff'
},
reviewSectionTitle: {
margin: '0 0 10px',
fontSize: '16px',
color: '#1f2937'
},
reviewMediaList: {
display: 'grid',
gap: '10px',
marginTop: '10px'
},
reviewMediaItem: {
display: 'grid',
gridTemplateColumns: '120px minmax(0, 1fr)',
gap: '12px',
alignItems: 'center',
padding: '10px',
background: '#f8fafc',
border: '1px solid #e2e8f0',
borderRadius: '6px'
},
reviewMediaImage: {
width: '120px',
height: '80px',
objectFit: 'cover',
borderRadius: '4px',
background: '#e5e7eb'
},
faqItem: {
padding: '10px',
background: '#f8fafc',
borderRadius: '6px',
marginBottom: '8px'
},
notice: {
padding: '10px 12px',
background: '#eef6ff',
color: '#1e4f8a',
borderRadius: '6px'
},
filePreviewLayout: {
display: 'grid',
gridTemplateColumns: 'minmax(220px, 32%) minmax(0, 1fr)',
gap: '14px'
},
fileList: {
maxHeight: '420px',
overflow: 'auto',
border: '1px solid #e2e8f0',
borderRadius: '6px',
background: '#f8fafc'
},
fileButton: {
display: 'block',
width: '100%',
padding: '9px 10px',
border: 'none',
borderBottom: '1px solid #e2e8f0',
background: 'transparent',
textAlign: 'left',
cursor: 'pointer',
fontSize: '13px',
overflowWrap: 'anywhere'
},
activeFileButton: {
background: '#dbeafe',
fontWeight: '700'
},
folderButton: {
color: '#475569',
cursor: 'default',
fontWeight: '700'
},
fileContent: {
minHeight: '220px',
maxHeight: '520px',
overflow: 'auto',
padding: '12px',
border: '1px solid #e2e8f0',
borderRadius: '6px',
background: '#ffffff'
},
readme: {
background: '#f8f9fa',
padding: '15px',
borderRadius: '4px',
overflow: 'auto',
fontSize: '14px',
whiteSpace: 'pre-wrap'
},
mediaGrid: {
display: 'grid',
gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
gap: '10px',
marginTop: '10px'
},
mediaThumbnail: {
padding: '20px',
background: '#ecf0f1',
borderRadius: '4px',
textAlign: 'center',
fontSize: '12px'
},
emptyState: {
textAlign: 'center',
padding: '60px 20px',
color: '#999'
}
};

export default AdminDashboard;
